/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import React, {
    useContext, useEffect, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from 'shared/components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from 'shared/components/ui/dialog';
import {
    Loader2, Plus, Pencil, Brain, Check, AlertCircle, RefreshCw, Cpu, Play, Maximize2, Minimize2, X, Code, Search
} from 'lucide-react';
import {
    cn
} from 'shared/lib/utils';
import {
    Context
} from 'shared/context/GlobalState';
import {
    instructionService
} from 'shared/api/services/BEX/instruction.service';
import {
    providerParameterDefaults
} from 'builders/builderInternal/components/Common/providerConfigRegistry';
import ModelCatalogModal from './ModelCatalogModal';
import {
    modelService
} from 'shared/api/services/BEX/models.services';
import ProviderParamEditor from './ProviderParamEditor';
import InstructionTestPanel from '../../components/InstructionTestPanel';
import EntityJsonView from '../../components/EntityJsonView';
import {
    sortInstructionsNewestFirst
} from '../../library/instructionListUtils';
import {
    LlmLabeled, HelpTooltip
} from '../inline-steps/llmStepLayout';
import {
    INSTRUCTION_FIELD_HELP
} from './providerParameterHelp';

/**
 * InstructionPicker — chip with Change / Edit / + New popups.
 *
 * Matches the prototype pattern: shows a chip for the currently-selected
 * instruction (name + model_type), with three actions:
 *   - Change → modal listing all sub-org instructions to pick from
 *   - Edit   → modal form to edit the currently-selected instruction
 *   - + New  → modal form to create a new one (auto-saved to Library)
 *
 * Saves through `instructionService` so anything created/edited shows up
 * in the Library Instructions tab automatically.
 */

// Provider list — derived from the registry that backs the per-provider
// parameter editor. Keep "Custom" as an escape hatch for a free-text model id.
const MODEL_TYPES = [...Object.keys(providerParameterDefaults), 'Custom'];

export default function InstructionPicker({
    value, onChange, showLabel = true
}) {
    const {
        oragID, subOragID
    } = useParams();
    const {
        user
    } = useContext(Context);
    const [list, setList] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [query, setQuery] = useState('');
    const [pickerOpen, setPickerOpen] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [formMode, setFormMode] = useState('new'); // 'new' | 'edit'
    const [loadingEdit, setLoadingEdit] = useState(false);
    const [formInitial, setFormInitial] = useState(null);

    /** Fetch all instructions in the user's sub-org.
     *
     * Filter out malformed / orphan rows: the Redis list endpoint can return
     * empty `{}` rows left over from failed creates, and those would
     * otherwise render as blank cards in the picker. A valid row must at
     * minimum have a `unique_name` and `model_type` (the selection key).
     */
    async function refresh() {
        if (!oragID || !subOragID) return;
        setLoadingList(true);
        try {
            const res = await instructionService.listModels(oragID, subOragID, null);
            const arr = res?.data || res || [];
            const valid = (Array.isArray(arr) ? arr : []).filter(
                (x) => x && typeof x === 'object' && x.unique_name && x.model_type
            );
            setList(sortInstructionsNewestFirst(valid));
        } finally {
            setLoadingList(false);
        }
    }

    useEffect(() => { refresh(); }, [oragID, subOragID]);

    const selected = list.find(
        x => x.unique_name === value?.unique_name && x.model_type === value?.model_type
    );

    const filtered = (() => {
        const q = query.trim().toLowerCase();
        if (!q) return list;
        return list.filter((inst) =>
            [inst.unique_name, inst.model_type, inst.modelId, inst.modelDescription]
                .filter(Boolean)
                .join(' ')
                .toLowerCase()
                .includes(q)
        );
    })();

    function openPicker() { setQuery(''); refresh(); setPickerOpen(true); }
    function openNew() {
        const defaultProvider = 'Groq';
        setFormInitial({
            unique_name: '',
            model_type: defaultProvider,
            modelId: '',
            prompt: '',
            modelDescription: '',
            parameters: {
                ...(providerParameterDefaults[defaultProvider] || {})
            }
        });
        setFormMode('new');
        setFormOpen(true);
    }
    async function openEdit() {
        if (!value?.unique_name || !value?.model_type) return;
        setLoadingEdit(true);
        try {
            const res = await instructionService.getModel(oragID, subOragID, value.model_type, value.unique_name);
            const full = res?.data || res;
            if (!full) return;
            setFormInitial(full);
            setFormMode('edit');
            setFormOpen(true);
        } catch { /* silently ignore — nothing to open */ }
        finally { setLoadingEdit(false); }
    }

    function pickFromList(inst) {
        onChange({
            unique_name: inst.unique_name,
            model_type: inst.model_type
        });
        setPickerOpen(false);
    }
    function onFormSaved(saved) {
        // Saved instruction → select it + refresh list
        onChange({
            unique_name: saved.unique_name,
            model_type: saved.model_type
        });
        setFormOpen(false);
        refresh();
    }

    return (
        <div>
            {showLabel && (
                <div className="ds-meta" style={{
                    marginBottom: 6
                }}>Instruction</div>
            )}
            <div className="ds-pick-chip">
                <span className="ds-pick-chip-icon ds-tone-violet">
                    <Brain size={15} />
                </span>
                <div className="ds-pick-chip-body">
                    {value?.unique_name ? (
                        <>
                            <div className="ds-pick-chip-title">{value.unique_name}</div>
                            <div className="ds-pick-chip-meta">
                                {value.model_type}{selected?.modelId ? ` · ${selected.modelId}` : ''}
                                {!selected && ' · not in your library'}
                            </div>
                        </>
                    ) : (
                        <div className="ds-pick-chip-meta" style={{
                            fontStyle: 'italic'
                        }}>
                            No instruction selected.
                        </div>
                    )}
                </div>
                <div className="ds-pick-chip-actions">
                    <Button variant="outline" size="sm" type="button" onClick={openPicker}>
                        Change
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={openEdit}
                        disabled={!value?.unique_name || loadingEdit}
                        title={value?.unique_name ? 'Edit this instruction' : 'Pick or create one first'}
                    >
                        {loadingEdit
                            ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            : <Pencil className="mr-1 h-3 w-3" />}
                        Edit
                    </Button>
                    <Button size="sm" type="button" onClick={openNew}>
                        <Plus className="mr-1 h-3 w-3" /> New
                    </Button>
                </div>
            </div>
            <p className="ds-help" style={{
                marginTop: 6
            }}>
                Saved into your sub-org's Library — visible under <b>Library → Instructions</b>.
            </p>

            {/* Picker dialog */}
            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Pick an instruction</DialogTitle>
                        <DialogDescription>
                            Lists every Instruction in this sub-org's Library. Pick one or create new.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search by name, provider, or model…"
                                className="h-8 pl-7 text-[12.5px]"
                            />
                        </div>
                        <Button variant="outline" size="sm" onClick={refresh} disabled={loadingList}>
                            <RefreshCw className={cn('mr-1 h-3 w-3', loadingList && 'animate-spin')} /> Refresh
                        </Button>
                        <Button size="sm" onClick={() => { setPickerOpen(false); openNew(); }}>
                            <Plus className="mr-1 h-3 w-3" /> New instruction
                        </Button>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {filtered.length}{filtered.length !== list.length ? ` of ${list.length}` : ''} in library
                        </span>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto rounded border">
                        {loadingList && (
                            <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                            </div>
                        )}
                        {!loadingList && list.length === 0 && (
                            <div className="p-6 text-center text-sm text-muted-foreground">
                                No instructions in this sub-org yet.
                            </div>
                        )}
                        {!loadingList && list.length > 0 && filtered.length === 0 && (
                            <div className="p-6 text-center text-sm text-muted-foreground">
                                No instructions match &ldquo;{query}&rdquo;.
                            </div>
                        )}
                        {!loadingList && filtered.map((inst) => {
                            const sel = inst.unique_name === value?.unique_name && inst.model_type === value?.model_type;
                            return (
                                <button
                                    key={`${inst.model_type}:${inst.unique_name}`}
                                    type="button"
                                    onClick={() => pickFromList(inst)}
                                    className={cn('ds-pick-row', sel && 'ds-pick-row-selected')}
                                >
                                    <span className="ds-pick-row-icon ds-tone-violet">
                                        <Brain size={14} />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <span className="truncate font-semibold text-sm">{inst.unique_name}</span>
                                            <span className="ds-badge ds-badge-neutral">
                                                {inst.model_type}
                                            </span>
                                            {sel && <Check className="h-3.5 w-3.5" style={{
                                                color: 'var(--ds-primary)'
                                            }} />}
                                        </div>
                                        <div className="ds-pick-chip-meta" style={{
                                            marginTop: 2
                                        }}>
                                            {inst.modelId || '—'}
                                        </div>
                                        {inst.modelDescription && (
                                            <div style={{
                                                marginTop: 4,
                                                fontSize: 'var(--ds-text-xs)',
                                                color: 'var(--ds-text-muted)',
                                                lineHeight: 1.5,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden'
                                            }}>
                                                {inst.modelDescription}
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit/Create form dialog */}
            {formOpen && (
                <InstructionFormDialog
                    open={formOpen}
                    onOpenChange={setFormOpen}
                    mode={formMode}
                    initial={formInitial}
                    onSaved={onFormSaved}
                />
            )}
        </div>
    );
}

/* ---------------- Form Dialog ---------------- */

function InstructionFormSection({
    title, description, children, className
}) {
    return (
        <section
            className={cn(
                'rounded-lg border border-border/80 bg-card/40 p-3 shadow-sm',
                className
            )}
        >
            <div className="mb-2 border-b border-dashed border-border/60 pb-2">
                <h3 className="text-[12px] font-semibold text-foreground">{title}</h3>
                {description && (
                    <p className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground">{description}</p>
                )}
            </div>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

export function InstructionFormDialog({
    open, onOpenChange, mode, initial, onSaved
}) {
    const {
        oragID, subOragID
    } = useParams();
    const {
        user, addNewNotifcation
    } = useContext(Context);
    const [uniqueName, setUniqueName] = useState(initial?.unique_name || '');
    const [modelType, setModelType] = useState(initial?.model_type || 'Groq');
    const [modelId, setModelId] = useState(initial?.modelId || '');
    const [prompt, setPrompt] = useState(initial?.prompt || '');
    const [description, setDescription] = useState(initial?.modelDescription || '');
    const [parameters, setParameters] = useState(
        (initial?.parameters && typeof initial.parameters === 'object')
            ? {
                ...initial.parameters
            }
            : {
                ...(providerParameterDefaults[initial?.model_type || 'Groq'] || {})
            }
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [catalogOpen, setCatalogOpen] = useState(false);
    const [showTest, setShowTest] = useState(false);
    const [testMaximized, setTestMaximized] = useState(false);
    const [showJson, setShowJson] = useState(false);
    // Live model catalog (server-fetched) — used to auto-detect the provider
    // when a model id is typed manually. Same source the picker modal uses, so
    // every backend provider (incl. Novita) is covered, not a static snapshot.
    const [catalog, setCatalog] = useState([]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await modelService.getModels({
                    provider: ['All'],
                    filter: {
                        Watson: {
                            region: 'All',
                            showInactive: false
                        },
                        Groq: {
                            showInactive: false
                        },
                        SambaNova: {
                            showInactive: false
                        },
                        OnPrem: {
                            showInactive: false
                        },
                        Gemini: {
                            showInactive: false
                        },
                        Cerebras: {
                            showInactive: false
                        },
                        Mistral: {
                            showInactive: false
                        },
                        Novita: {
                            showInactive: false
                        },
                    },
                }, false, oragID);
                if (!cancelled) setCatalog(Array.isArray(res?.data) ? res.data : []);
            } catch {
                if (!cancelled) setCatalog([]);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Dirty tracking — compare form state to a snapshot of `initial`. The
    // test pane uses this to warn the user when their pending edits won't
    // affect the run (predict reads the SAVED version of the instruction).
    const initialSnapshot = React.useMemo(() => JSON.stringify({
        uniqueName: initial?.unique_name || '',
        modelType: initial?.model_type || 'Groq',
        modelId: initial?.modelId || '',
        prompt: initial?.prompt || '',
        description: initial?.modelDescription || '',
        parameters: (initial?.parameters && typeof initial.parameters === 'object')
            ? initial.parameters
            : (providerParameterDefaults[initial?.model_type || 'Groq'] || {})
    }), [initial]);
    const currentSnapshot = JSON.stringify({
        uniqueName,
        modelType,
        modelId,
        prompt,
        description,
        parameters: parameters || {}
    });
    const pristine = currentSnapshot === initialSnapshot;

    function applyProvider(nextProvider) {
        setModelType(nextProvider);
        // Reset parameters to provider defaults so we don't carry over fields
        // that don't apply to the new provider.
        const def = providerParameterDefaults[nextProvider];
        if (def) setParameters({
            ...def
        });
    }

    function handleCatalogPick({
        model_id, provider_type
    }) {
        setModelId(model_id);
        // Auto-set provider from the catalog. Only reset parameters when the
        // provider actually changes — otherwise keep the user's edits.
        if (provider_type && provider_type !== modelType) {
            applyProvider(provider_type);
        }
    }

    async function handleSave({
        silent = false
    } = {}) {
        setError(null);
        const failValidation = (message) => {
            setError(message);
            addNewNotifcation(message, 'danger');
            return false;
        };
        if (!uniqueName.trim()) return failValidation('Name is required.');
        if (!modelType) return failValidation('Model type is required.');
        if (!modelId.trim()) return failValidation('Model ID is required.');
        if (!prompt.trim()) return failValidation('Prompt is required.');
        setSaving(true);
        try {
            // On edit, the record lives under its ORIGINAL provider's Redis
            // namespace. The server keys the update by `model_type`, so it must
            // be the original type; switching providers is signalled separately
            // via `new_model_type` (see redisUpdateInstruction — it then moves
            // the record, re-points opcodes, and deletes the old one). Sending
            // the new provider as `model_type` 404s ("<Provider>Model … does
            // not exist"). Mirrors the legacy builder's UpdateInstruction flow.
            const originalType = (mode === 'edit' && initial?.model_type)
                ? initial.model_type
                : modelType;
            const payload = {
                unique_name: uniqueName.trim(),
                model_type: originalType,
                modelId: modelId.trim(),
                prompt,
                modelDescription: description,
                parameters: parameters || {},
                org_id: oragID,
                sub_org_id: subOragID,
                user_id: user?._id,
            };
            if (mode === 'edit') {
                const updatePayload = {
                    ...payload,
                    instructionId: initial?._id
                };
                // Provider changed → tell the server to move the instruction.
                if (modelType !== originalType) {
                    updatePayload.new_model_type = modelType;
                    if (initial?.creation_timestamp) {
                        updatePayload.creation_timestamp = initial.creation_timestamp;
                    }
                }
                await instructionService.updateModel(updatePayload);
            } else {
                await instructionService.createModel(payload);
            }
            if (!silent) {
                onSaved({
                    unique_name: uniqueName.trim(),
                    model_type: modelType,
                    modelId: modelId.trim()
                });
            }
            return true;
        } catch (err) {
            const message = err?.response?.data?.message
                || err?.response?.data?.error
                || err?.message
                || 'Save failed.';
            setError(message);
            addNewNotifcation(message, 'danger');
            return false;
        } finally {
            setSaving(false);
        }
    }

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent
                    className={cn(
                        'flex flex-col gap-0 overflow-hidden p-0',
                        showTest
                            ? 'h-[88vh] max-h-[88vh] w-[95vw] max-w-[1400px]'
                            : 'h-[min(90vh,920px)] max-h-[90vh] w-[calc(100%-2rem)] max-w-3xl'
                    )}
                >
                    <DialogHeader className="px-5 pt-4 pb-3 pr-12 border-b border-[var(--ds-divider)] flex-shrink-0">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 flex-1">
                                <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
                                    {mode === 'edit' ? 'Edit instruction' : 'New instruction'}
                                    {showTest && !pristine && (
                                        <span className="rounded-full border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900">
                                            Unsaved
                                        </span>
                                    )}
                                </DialogTitle>
                                <DialogDescription className="mt-1 text-[12px]">
                                    Saved into <b>Library → Instructions</b> for this sub-org.
                                </DialogDescription>
                            </div>
                            <div className="flex flex-shrink-0 items-center gap-1.5">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setShowJson(true)}
                                    title="View JSON"
                                    className="h-8"
                                >
                                    <Code className="mr-1 h-3 w-3" />
                                    JSON
                                </Button>
                                {mode === 'edit' && (
                                    <>
                                        <Button
                                            type="button"
                                            variant={showTest ? 'secondary' : 'outline'}
                                            size="sm"
                                            onClick={() => setShowTest((v) => !v)}
                                            title={showTest ? 'Hide test panel' : 'Open test panel'}
                                            className="h-8"
                                        >
                                            <Play className="mr-1 h-3 w-3" />
                                            {showTest ? 'Hide test' : 'Test'}
                                        </Button>
                                        {showTest && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setTestMaximized((v) => !v)}
                                                title={testMaximized ? 'Restore split view' : 'Maximize test panel'}
                                                className="h-8 px-2"
                                            >
                                                {testMaximized
                                                    ? <Minimize2 className="h-4 w-4" />
                                                    : <Maximize2 className="h-4 w-4" />}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </DialogHeader>
                    <div
                        className={cn(
                            'flex min-h-0 flex-1 overflow-hidden',
                            showTest && 'flex-row'
                        )}
                    >
                        {/* Form pane — scrollable body */}
                        <div
                            className={cn(
                                'min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-3 space-y-3',
                                showTest && !testMaximized && 'min-w-0 max-w-[50%] border-r',
                                showTest && testMaximized && 'hidden'
                            )}
                        >
                            <InstructionFormSection
                                title="Basics"
                                description="Unique key in Library and an optional note for your team."
                            >
                                <LlmLabeled label="Name (unique)" help={INSTRUCTION_FIELD_HELP.name}>
                                    <Input
                                        value={uniqueName}
                                        onChange={(e) => setUniqueName(e.target.value)}
                                        placeholder="e.g. customer_support_v1"
                                        disabled={mode === 'edit'}
                                        className="font-mono text-sm h-9"
                                    />
                                    {mode === 'edit' && (
                                        <p className="text-[10.5px] text-muted-foreground">
                                            Name is the key — cannot change after create.
                                        </p>
                                    )}
                                </LlmLabeled>
                                <LlmLabeled label="Description" help={INSTRUCTION_FIELD_HELP.description}>
                                    <Input
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="What this instruction is for"
                                        className="h-9"
                                    />
                                </LlmLabeled>
                            </InstructionFormSection>

                            <InstructionFormSection
                                title="Model & provider"
                                description="Pick from the catalog or type a model id — provider is set automatically when possible."
                            >
                                <div className="flex flex-wrap items-center gap-2 rounded-md border bg-card px-2.5 py-2">
                                    <span className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-violet-50 text-violet-600">
                                        <Cpu className="h-3.5 w-3.5" />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        {modelId ? (
                                            <>
                                                <div className="truncate font-mono text-sm font-semibold" title={modelId}>
                                                    {modelId}
                                                </div>
                                                <div className="text-[11px] text-muted-foreground">{modelType}</div>
                                            </>
                                        ) : (
                                            <div className="text-sm text-muted-foreground">No model selected</div>
                                        )}
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        className="h-8"
                                        onClick={() => setCatalogOpen(true)}
                                    >
                                        {modelId ? 'Change model' : 'Pick from catalog'}
                                    </Button>
                                </div>
                                <LlmLabeled label="Model id" help={INSTRUCTION_FIELD_HELP.model}>
                                    <Input
                                        value={modelId}
                                        onChange={(e) => {
                                            const next = e.target.value;
                                            setModelId(next);
                                            const hit = catalog.find((m) => m.model_id === next.trim());
                                            if (hit?.provider_type && hit.provider_type !== modelType) {
                                                applyProvider(hit.provider_type);
                                            }
                                        }}
                                        placeholder="Or type a model id manually"
                                        className="font-mono text-[12.5px] h-9"
                                    />
                                </LlmLabeled>
                                <LlmLabeled label="Provider" help={INSTRUCTION_FIELD_HELP.provider}>
                                    <Select
                                        value={modelType}
                                        onValueChange={(v) => { applyProvider(v); }}
                                        disabled={mode === 'edit'}
                                    >
                                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                            {MODEL_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </LlmLabeled>
                            </InstructionFormSection>

                            <InstructionFormSection
                                title="Prompt"
                                description="System instruction text, sent to the model exactly as written. Runtime input arrives through the LLM step's query."
                            >
                                <LlmLabeled label="Prompt" help={INSTRUCTION_FIELD_HELP.prompt}>
                                    <textarea
                                        value={prompt}
                                        onChange={(e) => setPrompt(e.target.value)}
                                        placeholder={'## Instruction\nYou are a …\n\n## Context\n…\n\n## Sample Input\n…\n\n## Sample Output\n…'}
                                        rows={6}
                                        className="min-h-[112px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono leading-snug resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                                    />
                                </LlmLabeled>
                            </InstructionFormSection>

                            <details className="group rounded-lg border border-border/80 bg-muted/15 px-3 py-2 open:pb-3">
                                <summary className="flex cursor-pointer list-none items-center gap-1.5 py-0.5 text-[12px] font-semibold text-foreground [&::-webkit-details-marker]:hidden">
                                    <span>Provider parameters</span>
                                    <span className="text-[11px] font-normal text-muted-foreground">· {modelType}</span>
                                    <HelpTooltip
                                        content={INSTRUCTION_FIELD_HELP.providerParams}
                                        ariaLabel="About provider parameters"
                                    />
                                    <span className="ml-auto text-[10.5px] font-normal text-muted-foreground group-open:hidden">
                                        Expand to tune
                                    </span>
                                </summary>
                                <div className="mt-2 border-t border-dashed border-border/60 pt-2">
                                    <ProviderParamEditor
                                        provider={modelType}
                                        value={parameters}
                                        onChange={setParameters}
                                    />
                                </div>
                            </details>

                            {error && (
                                <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2.5 text-xs text-red-900">
                                    <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                                    <div>{error}</div>
                                </div>
                            )}
                        </div>

                        {/* Test pane — only mounted in edit mode + when toggled on. */}
                        {showTest && mode === 'edit' && (
                            <div
                                className={cn(
                                    'flex min-h-0 flex-1 flex-col overflow-hidden',
                                    !testMaximized && 'min-w-0'
                                )}
                            >
                                {!pristine && (
                                    <div className="flex-shrink-0 border-b border-amber-300/50 bg-amber-50/60 px-3 py-1.5 text-[11px] text-amber-900">
                                        <AlertCircle className="mr-1 inline h-3 w-3" />
                                        Unsaved edits — predict runs against the saved version. Save first to test the latest.
                                    </div>
                                )}
                                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                                    <InstructionTestPanel
                                        instruction={{
                                            unique_name: initial?.unique_name || uniqueName,
                                            model_type: initial?.model_type || modelType,
                                            modelId: initial?.modelId || modelId,
                                            prompt: initial?.prompt || prompt,
                                            parameters: initial?.parameters || parameters
                                        }}
                                        maximized={testMaximized}
                                        isDirty={!pristine}
                                        onSaveBeforeRun={() => handleSave({
                                            silent: true
                                        })}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="gap-2 px-5 py-2.5 border-t border-[var(--ds-divider)] bg-[var(--ds-surface-2)] flex-shrink-0 sm:justify-end">
                        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving
                                ? <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                : mode === 'edit' ? <Check className="mr-1 h-3 w-3" /> : null}
                            {mode === 'edit' ? 'Save changes' : 'Create instruction'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <ModelCatalogModal
                open={catalogOpen}
                onOpenChange={setCatalogOpen}
                currentModelId={modelId}
                currentProviderType={modelType}
                onPick={handleCatalogPick}
                organizationId={oragID}
            />
            <EntityJsonView
                open={showJson}
                onOpenChange={setShowJson}
                title="Instruction JSON"
                entity={{
                    unique_name: uniqueName,
                    model_type: modelType,
                    modelId,
                    prompt,
                    modelDescription: description,
                    parameters: parameters || {}
                }}
                onApply={(d) => {
                    if (!d || typeof d !== 'object') return;
                    if (typeof d.unique_name === 'string') setUniqueName(d.unique_name);
                    if (typeof d.model_type === 'string') setModelType(d.model_type);
                    if (typeof d.modelId === 'string') setModelId(d.modelId);
                    if (typeof d.prompt === 'string') setPrompt(d.prompt);
                    if (typeof d.modelDescription === 'string') setDescription(d.modelDescription);
                    if (d.parameters && typeof d.parameters === 'object') {
                        setParameters({
                            ...d.parameters
                        });
                    }
                }}
            />
        </>
    );
}
